import { useState, useMemo, useEffect, useCallback, useRef, type ReactNode } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
    DragEndEvent,
} from '@dnd-kit/core';
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    useSortable,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import { authClient } from '../../utils/amplifyClient';
import { useUserContext } from '../../contexts/UserContext';
import { useUserModule, UserModuleProvider } from '../../contexts/UserModuleContext';
import { useEquipmentContext } from '../../contexts/EquipmentContext';
import { AccordionCard, Btn, Card, DataTable, IconPencil, IconPlus, IconTrashEmpty, PageHeader, SearchSuggest, TabNav, getTabPanelProps, type AccordionCardItem, type DataTableColumn } from '../ui';
import { SKILL_LEVEL_COLORS, COLOR_OPTIONS, DEFAULT_LEVEL_COLORS, sortSkillLevels } from '../../utils/skillLevelColors';
import type { SkillCategory } from '../../types/userModuleTypes';
import type { EquipmentCategory } from '../../types/schema';
import { EquipmentCategoryFormOverlay } from '../pages-equipment/EquipmentCategoryFormOverlay';
import { createSingleUnitMutation } from '../pages-equipment/equipmentMutations';
import { sanitizeGen2SingleUnitWritePayload } from '../pages-equipment/equipmentFieldUtils';
import { getMachineSuggestOptions } from '../../utils/skillsetMachineSuggestOptions';
import type { User } from '../../types/schema';
import { isUnlinkedInvite } from '../../utils/preferredCompany';
import { LOCAL_STORAGE_KEYS } from '../../utils/constants';

import '../../css/SkillsetSetupLibraryPage.css';

const SKILLSETS_TAB_NAV_ID = 'user-skillsets';
const SKILLSETS_TABS = [
    { id: 'classifications', label: 'Classifications' },
    { id: 'user-proficiencies', label: 'Grade User Skillsets' },
] as const;
type SkillsetsPageTab = (typeof SKILLSETS_TABS)[number]['id'];

const getGradeUserPath = (userId: string) => `/user-skillsets/${userId}`;

/** Selected MACHINE # entries (SingleUnit id, or legacy SubCategory id on edit hydrate). */
type MachinePick = { id: string; name: string };

type EquipmentUnitOption = { value: string; label: string };

const ADD_NEW_MACHINE_OPTION_LABEL = '+ Add new machine';

/** Dismisses only when mousedown and mouseup both occur on the backdrop (not after text-selection drags). */
function SslModalOverlay({
    onDismiss,
    className,
    children,
}: {
    onDismiss: () => void;
    className?: string;
    children: ReactNode;
}) {
    const dismissOnClickRef = useRef(false);

    return (
        <div
            className={['sslp-modal-overlay', className].filter(Boolean).join(' ')}
            onMouseDown={(e) => {
                dismissOnClickRef.current = e.target === e.currentTarget;
            }}
            onClick={(e) => {
                if (dismissOnClickRef.current && e.target === e.currentTarget) {
                    onDismiss();
                }
                dismissOnClickRef.current = false;
            }}
        >
            {children}
        </div>
    );
}

interface MachinePillsSearchSuggestProps {
    skillCategoryId: string;
    equipmentCategoryId: string | null | undefined;
    equipmentCategoryName: string;
    machineOptions: EquipmentUnitOption[];
    selected: MachinePick[];
    onSelectedChange: (next: MachinePick[]) => void;
    inputId: string;
    onLinkEquipmentCategory: (equipmentCategoryId: string) => Promise<void>;
}

function MachinePillsSearchSuggest({
    skillCategoryId: _skillCategoryId,
    equipmentCategoryId: initialEquipmentCategoryId,
    equipmentCategoryName: initialEquipmentCategoryName,
    machineOptions,
    selected,
    onSelectedChange,
    inputId,
    onLinkEquipmentCategory,
}: MachinePillsSearchSuggestProps) {
    const { company, companyOwner, companyUser } = useUserContext();
    const { addUnit, refreshEquipment, companyEquipmentCategories } = useEquipmentContext();

    const [equipmentCategoryId, setEquipmentCategoryId] = useState(initialEquipmentCategoryId ?? null);
    const [showCategoryOverlay, setShowCategoryOverlay] = useState(false);
    const [showUnitModal, setShowUnitModal] = useState(false);
    const [pendingUnitName, setPendingUnitName] = useState('');
    const [pendingCategoryName, setPendingCategoryName] = useState('');
    const [unitName, setUnitName] = useState('');
    const [isSavingUnit, setIsSavingUnit] = useState(false);
    const [unitSaveError, setUnitSaveError] = useState<string | null>(null);

    useEffect(() => {
        setEquipmentCategoryId(initialEquipmentCategoryId ?? null);
    }, [initialEquipmentCategoryId]);

    const equipmentCategoryName =
        initialEquipmentCategoryName ||
        (equipmentCategoryId
            ? companyEquipmentCategories.find((c) => c.id === equipmentCategoryId)?.name || ''
            : '');

    const selectedIds = new Set(selected.map((s) => s.id));
    const availableOptions = machineOptions.filter((o) => !selectedIds.has(o.value));

    const handleAddNewClick = ({ searchText }: { searchText: string }) => {
        const typed = searchText.trim();
        setPendingUnitName(typed);
        setUnitSaveError(null);
        if (equipmentCategoryId) {
            setUnitName(typed);
            setShowUnitModal(true);
        } else {
            setPendingCategoryName(typed);
            setShowCategoryOverlay(true);
        }
    };

    const handleCategoryCreated = async (category: EquipmentCategory) => {
        try {
            await onLinkEquipmentCategory(category.id);
            setEquipmentCategoryId(category.id);
            setShowCategoryOverlay(false);
            setPendingCategoryName('');
            setUnitName(pendingUnitName || category.name || '');
            setShowUnitModal(true);
        } catch (error) {
            console.error('Failed to link equipment category to skill category:', error);
        }
    };

    const handleCreateUnit = async () => {
        const trimmedName = unitName.trim();
        if (!trimmedName || !equipmentCategoryId) return;

        const companyId = (company?.id as string) || companyOwner?.companyId || '';
        if (!companyId) {
            setUnitSaveError('Company context is not available.');
            return;
        }

        setUnitSaveError(null);
        setIsSavingUnit(true);

        try {
            const today = new Date().toISOString().split('T')[0];
            const unitInputRaw: Record<string, unknown> = {
                name: trimmedName,
                categoryId: equipmentCategoryId,
                inputDate: today,
                inputByPerson: companyOwner?.name || companyUser?.name || '',
                equipmentStatus: true,
            };
            const unitInput = sanitizeGen2SingleUnitWritePayload(unitInputRaw, { dropEmptyStrings: true });

            const unitResponse = (await authClient.graphql({
                query: createSingleUnitMutation,
                variables: { input: unitInput },
            })) as { data?: { createSingleUnit?: { id: string; name?: string | null } }; errors?: Array<{ message?: string }> };

            const newUnit = unitResponse.data?.createSingleUnit;
            if (!newUnit?.id) {
                setUnitSaveError(unitResponse.errors?.[0]?.message || 'Failed to create equipment unit.');
                return;
            }

            addUnit(newUnit as Parameters<typeof addUnit>[0]);
            await refreshEquipment();

            const displayName = (newUnit.name && newUnit.name.trim()) || trimmedName;
            if (!selectedIds.has(newUnit.id)) {
                onSelectedChange([...selected, { id: newUnit.id, name: displayName }]);
            }

            setShowUnitModal(false);
            setUnitName('');
            setPendingUnitName('');
        } catch (error) {
            console.error('Failed to create equipment unit:', error);
            const message =
                (error as { errors?: Array<{ message?: string }> })?.errors?.[0]?.message ||
                (error instanceof Error ? error.message : null);
            setUnitSaveError(message || 'Failed to create equipment unit.');
        } finally {
            setIsSavingUnit(false);
        }
    };

    return (
        <>
            <label className="sslp-modal-label" htmlFor={inputId}>
                MACHINE # <span className="sslp-optional">Optional</span>
            </label>
            <div className="sslp-machine-select">
                {selected.length > 0 && (
                    <div className="sslp-pill-input-container sslp-machine-pills-only" role="list">
                        {selected.map((pill) => (
                            <span key={pill.id} className="sslp-pill-tag" role="listitem">
                                {pill.name}
                                <button
                                    type="button"
                                    aria-label={`Remove ${pill.name}`}
                                    onClick={() => onSelectedChange(selected.filter((p) => p.id !== pill.id))}
                                >
                                    ×
                                </button>
                            </span>
                        ))}
                    </div>
                )}
                <SearchSuggest
                    key={selected.map((s) => s.id).join(',') || 'none'}
                    options={availableOptions}
                    value=""
                    onChange={(value) => {
                        if (!value) return;
                        const opt = machineOptions.find((o) => o.value === value);
                        if (!opt || selectedIds.has(opt.value)) return;
                        onSelectedChange([...selected, { id: opt.value, name: opt.label }]);
                    }}
                    placeholder="Search machines..."
                    emptyMessage="No matching machines"
                    allowNew={false}
                    actionOption={{
                        label: ADD_NEW_MACHINE_OPTION_LABEL,
                        typedOnly: true,
                        onClick: handleAddNewClick,
                    }}
                    clearable
                    inputId={inputId}
                    ariaLabel="Machine number"
                />
            </div>
            <p className="sslp-modal-hint">
                Search linked equipment units or existing machine labels. Type a new name and choose &ldquo;{ADD_NEW_MACHINE_OPTION_LABEL}&rdquo; to add equipment.
            </p>

            <EquipmentCategoryFormOverlay
                visible={showCategoryOverlay}
                defaultCategoryName={pendingCategoryName}
                onDismiss={() => {
                    setShowCategoryOverlay(false);
                    setPendingCategoryName('');
                }}
                onCategoryCreated={handleCategoryCreated}
            />

            {showUnitModal && (
                <SslModalOverlay
                    className="sslp-modal-overlay-nested"
                    onDismiss={() => {
                        if (!isSavingUnit) {
                            setShowUnitModal(false);
                            setUnitSaveError(null);
                        }
                    }}
                >
                    <div className="sslp-modal">
                        <div className="sslp-modal-header">
                            <h3>Add New Machine</h3>
                            <button
                                type="button"
                                className="sslp-modal-close"
                                disabled={isSavingUnit}
                                onClick={() => {
                                    setShowUnitModal(false);
                                    setUnitSaveError(null);
                                }}
                            >
                                ×
                            </button>
                        </div>
                        <div className="sslp-modal-body">
                            {equipmentCategoryName && (
                                <p className="sslp-modal-hint">
                                    Creates equipment in <strong>{equipmentCategoryName}</strong>.
                                </p>
                            )}
                            <label className="sslp-modal-label" htmlFor={`${inputId}-new-unit-name`}>
                                UNIT NAME *
                            </label>
                            <input
                                id={`${inputId}-new-unit-name`}
                                className="sslp-modal-input"
                                value={unitName}
                                onChange={(e) => setUnitName(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' && unitName.trim() && !isSavingUnit) {
                                        void handleCreateUnit();
                                    }
                                }}
                                autoFocus
                                disabled={isSavingUnit}
                            />
                            {unitSaveError && <p className="sslp-modal-error">{unitSaveError}</p>}
                        </div>
                        <div className="sslp-modal-footer">
                            <button
                                type="button"
                                className="sslp-modal-btn-cancel"
                                disabled={isSavingUnit}
                                onClick={() => {
                                    setShowUnitModal(false);
                                    setUnitSaveError(null);
                                }}
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                className="sslp-modal-btn-primary"
                                disabled={!unitName.trim() || isSavingUnit}
                                onClick={() => void handleCreateUnit()}
                            >
                                {isSavingUnit ? 'Saving…' : 'Add Machine'}
                            </button>
                        </div>
                    </div>
                </SslModalOverlay>
            )}
        </>
    );
}

const getExpandedClassificationsStorageKey = (companyId: string) =>
    `${LOCAL_STORAGE_KEYS.SKILLSETS_EXPANDED_CLASSIFICATIONS}:${companyId}`;

const readStoredExpandedClassifications = (companyId: string): string[] => {
    try {
        const raw = localStorage.getItem(getExpandedClassificationsStorageKey(companyId));
        if (!raw) return [];
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed)
            ? parsed.filter((id): id is string => typeof id === 'string' && id.length > 0)
            : [];
    } catch {
        return [];
    }
};

const writeStoredExpandedClassifications = (companyId: string, classificationIds: string[]) => {
    try {
        const key = getExpandedClassificationsStorageKey(companyId);
        if (classificationIds.length === 0) {
            localStorage.removeItem(key);
            return;
        }
        localStorage.setItem(key, JSON.stringify(classificationIds));
    } catch {
        // Ignore quota / private-mode failures; in-memory state still works for the session.
    }
};

const getGradeUserColumns = (
    navigate: (path: string) => void,
    nameFilter: string,
    onNameFilterChange: (value: string) => void,
): DataTableColumn<User>[] => [
    {
        id: 'user',
        header: 'User',
        filterColSpan: 2,
        filterLabel: 'Filter by user name',
        renderFilter: () => (
            <input
                type="search"
                value={nameFilter}
                placeholder="Type to filter by User name"
                aria-label="Type to filter by User name"
                onChange={(event) => onNameFilterChange(event.target.value)}
            />
        ),
        renderCell: (user) => user.name?.trim() || user.email || '—',
    },
    {
        id: 'actions',
        header: 'Actions',
        tdClassName: 'sslp-grade-users-actions',
        thClassName: 'sslp-grade-users-actions',
        mobileCardAction: true,
        mobileHideLabel: true,
        renderCell: (user) => {
            const href = getGradeUserPath(user.id);
            return (
                <div className="table-actions">
                    <Btn
                        variant="primary"
                        buttonSize="sm"
                        buttonAs="a"
                        href={href}
                        buttonText="Grade Skillsets"
                        buttonOnClick={(event) => {
                            event.preventDefault();
                            navigate(href);
                        }}
                    />
                </div>
            );
        },
    },
];

// ---------------------------------------------------------------------------
// Sortable Skill Level row + Drag handle (follows @dnd-kit pattern from SingleFormContent)
// ---------------------------------------------------------------------------

const SkillLevelDragHandle = (props: Record<string, any>) => (
    <div className="sslp-drag-handle" {...props} title="Drag to reorder">
        <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor">
            <circle cx="5" cy="3" r="1.5" />
            <circle cx="11" cy="3" r="1.5" />
            <circle cx="5" cy="8" r="1.5" />
            <circle cx="11" cy="8" r="1.5" />
            <circle cx="5" cy="13" r="1.5" />
            <circle cx="11" cy="13" r="1.5" />
        </svg>
    </div>
);

const SortableSkillLevelRow = ({
    id,
    children,
}: {
    id: string;
    children: (dragHandleProps: Record<string, any>) => React.ReactNode;
}) => {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
    const style: React.CSSProperties = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
        position: 'relative' as const,
        zIndex: isDragging ? 10 : undefined,
    };
    const dragHandleProps = { ...attributes, ...listeners };
    return (
        <div ref={setNodeRef} style={style}>
            {children(dragHandleProps)}
        </div>
    );
};

// ---------------------------------------------------------------------------
// Setup Library Modals (inline in this file for cohesion)
// ---------------------------------------------------------------------------

interface AddClassificationLibModalProps {
    visible: boolean;
    onClose: () => void;
    onSave: (name: string) => void;
}

const AddClassificationLibModal = ({ visible, onClose, onSave }: AddClassificationLibModalProps) => {
    const [name, setName] = useState('');

    const handleSave = () => {
        if (!name.trim()) return;
        onSave(name.trim());
        setName('');
        onClose();
    };

    if (!visible) return null;

    return (
        <SslModalOverlay onDismiss={onClose}>
            <div className="sslp-modal">
                <div className="sslp-modal-header">
                    <h3>Add New Classification</h3>
                    <button className="sslp-modal-close" onClick={onClose}>
                        ×
                    </button>
                </div>
                <div className="sslp-modal-body">
                    <label className="sslp-modal-label">CLASSIFICATION NAME *</label>
                    <input
                        className="sslp-modal-input"
                        placeholder="e.g. Heavy Equipment Operator"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') handleSave();
                        }}
                        autoFocus
                    />
                </div>
                <div className="sslp-modal-footer">
                    <button className="sslp-modal-btn-cancel" onClick={onClose}>
                        Cancel
                    </button>
                    <button className="sslp-modal-btn-primary" disabled={!name.trim()} onClick={handleSave}>
                        Add Classification
                    </button>
                </div>
            </div>
        </SslModalOverlay>
    );
};

interface AddCategoryLibModalProps {
    visible: boolean;
    classificationId: string;
    classificationName: string;
    onClose: () => void;
    onSave: (name: string, equipmentCategoryId: string | null) => void;
}

const AddCategoryLibModal = ({
    visible,
    classificationId: _classificationId,
    classificationName,
    onClose,
    onSave,
}: AddCategoryLibModalProps) => {
    const { companyEquipmentCategories } = useEquipmentContext();
    const [name, setName] = useState('');
    const [equipCatId, setEquipCatId] = useState('');

    const handleSave = () => {
        if (!name.trim()) return;
        const matchedCat = companyEquipmentCategories.find(
            (ec) => ec.id === equipCatId || ec.name === equipCatId,
        );
        onSave(name.trim(), matchedCat?.id || null);
        setName('');
        setEquipCatId('');
        onClose();
    };

    if (!visible) return null;

    return (
        <SslModalOverlay onDismiss={onClose}>
            <div className="sslp-modal">
                <div className="sslp-modal-header">
                    <h3>Add New Category</h3>
                    <button className="sslp-modal-close" onClick={onClose}>
                        ×
                    </button>
                </div>
                <div className="sslp-modal-body">
                    <p className="sslp-modal-context">
                        <strong>Classification:</strong> {classificationName}
                    </p>
                    <label className="sslp-modal-label">CATEGORY NAME *</label>
                    <input
                        className="sslp-modal-input"
                        placeholder="e.g. Loader Operator"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') handleSave();
                        }}
                        autoFocus
                    />
                    <p className="sslp-modal-hint">Must be unique within its parent classification.</p>

                    <label className="sslp-modal-label">
                        CONNECT AN EQUIPMENT CATEGORY <span className="sslp-optional">Optional</span>
                    </label>
                    <SearchSuggest
                        options={companyEquipmentCategories.map((ec) => ({ value: ec.id, label: ec.name }))}
                        value={equipCatId}
                        onChange={setEquipCatId}
                        placeholder="Select or type a category..."
                        allowNew
                        newLabel="+ Add new"
                        inputId="skillset-add-equip-category"
                        ariaLabel="Connect an equipment category"
                    />
                    <p className="sslp-modal-hint">
                        Connecting to an equipment category will show a list of the equipment assigned to that category.
                    </p>
                </div>
                <div className="sslp-modal-footer">
                    <button className="sslp-modal-btn-cancel" onClick={onClose}>
                        Cancel
                    </button>
                    <button className="sslp-modal-btn-primary" disabled={!name.trim()} onClick={handleSave}>
                        Add Category
                    </button>
                </div>
            </div>
        </SslModalOverlay>
    );
};

interface AddTaskLibModalProps {
    visible: boolean;
    categoryId: string;
    categoryName: string;
    classificationName: string;
    skillCategory: SkillCategory | undefined;
    onLinkEquipmentCategory: (equipmentCategoryId: string) => Promise<void>;
    onClose: () => void;
    onSave: (name: string, machineIds: string[], assignedUserIds: string[]) => void;
}

const AddTaskLibModal = ({
    visible,
    categoryId,
    categoryName,
    classificationName,
    skillCategory,
    onLinkEquipmentCategory,
    onClose,
    onSave,
}: AddTaskLibModalProps) => {
    const { allCompanyUsers } = useUserContext();
    const { subCategories } = useUserModule();
    const { companyUnits, companyEquipmentCategories } = useEquipmentContext();
    const [name, setName] = useState('');
    const [selectedMachines, setSelectedMachines] = useState<MachinePick[]>([]);
    const [assignedUsers, setAssignedUsers] = useState<Set<string>>(new Set());
    const [equipmentCategoryIdOverride, setEquipmentCategoryIdOverride] = useState<string | null | undefined>(undefined);

    useEffect(() => {
        if (visible) {
            setEquipmentCategoryIdOverride(undefined);
        }
    }, [visible, categoryId]);

    const effectiveSkillCategory = useMemo((): SkillCategory | undefined => {
        if (!skillCategory) return undefined;
        if (equipmentCategoryIdOverride !== undefined) {
            return { ...skillCategory, equipmentCategoryId: equipmentCategoryIdOverride };
        }
        return skillCategory;
    }, [skillCategory, equipmentCategoryIdOverride]);

    const machineOptions = useMemo(
        () => getMachineSuggestOptions(effectiveSkillCategory, companyUnits, subCategories),
        [effectiveSkillCategory, companyUnits, subCategories],
    );

    const equipmentCategoryName = useMemo(() => {
        const equipmentCategoryId = effectiveSkillCategory?.equipmentCategoryId;
        if (!equipmentCategoryId) return '';
        return companyEquipmentCategories.find((c) => c.id === equipmentCategoryId)?.name || '';
    }, [effectiveSkillCategory?.equipmentCategoryId, companyEquipmentCategories]);

    const handleLinkEquipmentCategory = async (equipmentCategoryId: string) => {
        await onLinkEquipmentCategory(equipmentCategoryId);
        setEquipmentCategoryIdOverride(equipmentCategoryId);
    };

    const handleToggleUser = (userId: string) => {
        setAssignedUsers((prev) => {
            const next = new Set(prev);
            if (next.has(userId)) {
                next.delete(userId);
            } else {
                next.add(userId);
            }
            return next;
        });
    };

    const handleToggleAll = () => {
        if (assignedUsers.size === allCompanyUsers.length) {
            setAssignedUsers(new Set());
        } else {
            setAssignedUsers(new Set(allCompanyUsers.map((u) => u.id)));
        }
    };

    const handleSave = () => {
        if (!name.trim()) return;
        onSave(
            name.trim(),
            selectedMachines.map((m) => m.id),
            Array.from(assignedUsers),
        );
        setName('');
        setSelectedMachines([]);
        setAssignedUsers(new Set());
        onClose();
    };

    const getInitials = (n: string | null | undefined): string => {
        if (!n) return '?';
        const parts = n.trim().split(' ');
        if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
        return (parts[0][0] || '?').toUpperCase();
    };

    if (!visible) return null;

    return (
        <SslModalOverlay onDismiss={onClose}>
            <div className="sslp-modal sslp-modal-wide">
                <div className="sslp-modal-header">
                    <h3>Add New Task</h3>
                    <button className="sslp-modal-close" onClick={onClose}>
                        ×
                    </button>
                </div>
                <div className="sslp-modal-body">
                    <p className="sslp-modal-context">
                        <strong>Category:</strong> {categoryName} &middot; <strong>Classification:</strong>{' '}
                        {classificationName}
                    </p>

                    <label className="sslp-modal-label" htmlFor="skillset-add-task-name">
                        TASK NAME *
                    </label>
                    <input
                        id="skillset-add-task-name"
                        className="sslp-modal-input"
                        placeholder="e.g. Load Haul Trucks"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' && name.trim()) handleSave();
                        }}
                        autoFocus
                    />
                    <p className="sslp-modal-hint">Must be unique within its parent category.</p>

                    <MachinePillsSearchSuggest
                        skillCategoryId={categoryId}
                        equipmentCategoryId={effectiveSkillCategory?.equipmentCategoryId}
                        equipmentCategoryName={equipmentCategoryName}
                        machineOptions={machineOptions}
                        selected={selectedMachines}
                        onSelectedChange={setSelectedMachines}
                        inputId="skillset-add-task-machine"
                        onLinkEquipmentCategory={handleLinkEquipmentCategory}
                    />

                    <label className="sslp-modal-label">ASSIGN TO USER</label>
                    <div className="sslp-user-toggle-list">
                        <div className="sslp-user-toggle-row sslp-toggle-all">
                            <span>Select All / None</span>
                            <button
                                type="button"
                                className={`sslp-toggle-switch ${assignedUsers.size === allCompanyUsers.length ? 'active' : ''}`}
                                onClick={handleToggleAll}
                            />
                        </div>
                        {allCompanyUsers.map((user) => (
                            <div key={user.id} className="sslp-user-toggle-row">
                                <div className="sslp-user-toggle-info">
                                    <div className="sslp-user-avatar-sm">{getInitials(user.name)}</div>
                                    <div>
                                        <div className="sslp-user-toggle-name">{user.name || '—'}</div>
                                        <div className="sslp-user-toggle-role">{user.role || ''}</div>
                                    </div>
                                </div>
                                <button
                                    type="button"
                                    className={`sslp-toggle-switch ${assignedUsers.has(user.id) ? 'active' : ''}`}
                                    onClick={() => handleToggleUser(user.id)}
                                />
                            </div>
                        ))}
                    </div>
                </div>
                <div className="sslp-modal-footer">
                    <button className="sslp-modal-btn-cancel" onClick={onClose}>
                        Cancel
                    </button>
                    <button className="sslp-modal-btn-primary" disabled={!name.trim()} onClick={handleSave}>
                        Add Task
                    </button>
                </div>
            </div>
        </SslModalOverlay>
    );
};

// ---------------------------------------------------------------------------
// Edit Classification Modal
// ---------------------------------------------------------------------------

interface EditClassificationLibModalProps {
    visible: boolean;
    classificationId: string;
    currentName: string;
    onClose: () => void;
    onSave: (id: string, name: string) => void;
}

const EditClassificationLibModal = ({
    visible,
    classificationId,
    currentName,
    onClose,
    onSave,
}: EditClassificationLibModalProps) => {
    const [name, setName] = useState(currentName);

    const handleSave = () => {
        if (!name.trim()) return;
        onSave(classificationId, name.trim());
        onClose();
    };

    // Reset when modal opens with new data
    if (visible && name !== currentName && name === '') {
        setName(currentName);
    }

    if (!visible) return null;

    return (
        <SslModalOverlay onDismiss={onClose}>
            <div className="sslp-modal">
                <div className="sslp-modal-header">
                    <h3>Edit Classification</h3>
                    <button className="sslp-modal-close" onClick={onClose}>
                        ×
                    </button>
                </div>
                <div className="sslp-modal-body">
                    <label className="sslp-modal-label">CLASSIFICATION NAME *</label>
                    <input
                        className="sslp-modal-input"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') handleSave();
                        }}
                        autoFocus
                    />
                </div>
                <div className="sslp-modal-footer">
                    <button className="sslp-modal-btn-cancel" onClick={onClose}>
                        Cancel
                    </button>
                    <button className="sslp-modal-btn-primary" disabled={!name.trim()} onClick={handleSave}>
                        Save
                    </button>
                </div>
            </div>
        </SslModalOverlay>
    );
};

// ---------------------------------------------------------------------------
// Edit Category Modal
// ---------------------------------------------------------------------------

interface EditCategoryLibModalProps {
    visible: boolean;
    categoryId: string;
    currentName: string;
    currentEquipmentCategoryId: string | null;
    classificationName: string;
    onClose: () => void;
    onSave: (id: string, name: string, equipmentCategoryId: string | null) => void;
}

const EditCategoryLibModal = ({
    visible,
    categoryId,
    currentName,
    currentEquipmentCategoryId,
    classificationName,
    onClose,
    onSave,
}: EditCategoryLibModalProps) => {
    const { companyEquipmentCategories } = useEquipmentContext();
    const [name, setName] = useState(currentName);
    const [equipCatId, setEquipCatId] = useState(currentEquipmentCategoryId || '');

    useEffect(() => {
        if (!visible) return;
        setName(currentName);
        setEquipCatId(currentEquipmentCategoryId || '');
    }, [visible, currentName, currentEquipmentCategoryId]);

    const handleSave = () => {
        if (!name.trim()) return;
        const matchedCat = companyEquipmentCategories.find(
            (ec) => ec.id === equipCatId || ec.name === equipCatId,
        );
        onSave(categoryId, name.trim(), matchedCat?.id || null);
        onClose();
    };

    if (!visible) return null;

    return (
        <SslModalOverlay onDismiss={onClose}>
            <div className="sslp-modal">
                <div className="sslp-modal-header">
                    <h3>Edit Category</h3>
                    <button className="sslp-modal-close" onClick={onClose}>
                        ×
                    </button>
                </div>
                <div className="sslp-modal-body">
                    <p className="sslp-modal-context">
                        <strong>Classification:</strong> {classificationName}
                    </p>
                    <label className="sslp-modal-label">CATEGORY NAME *</label>
                    <input
                        className="sslp-modal-input"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') handleSave();
                        }}
                        autoFocus
                    />
                    <p className="sslp-modal-hint">Must be unique within its parent classification.</p>

                    <label className="sslp-modal-label">
                        CONNECT AN EQUIPMENT CATEGORY <span className="sslp-optional">Optional</span>
                    </label>
                    <SearchSuggest
                        options={companyEquipmentCategories.map((ec) => ({ value: ec.id, label: ec.name }))}
                        value={equipCatId}
                        onChange={setEquipCatId}
                        placeholder="Select or type a category..."
                        allowNew
                        newLabel="+ Add new"
                        inputId="skillset-edit-equip-category"
                        ariaLabel="Connect an equipment category"
                    />
                    <p className="sslp-modal-hint">
                        Connecting to an equipment category will show a list of the equipment assigned to that category.
                    </p>
                </div>
                <div className="sslp-modal-footer">
                    <button className="sslp-modal-btn-cancel" onClick={onClose}>
                        Cancel
                    </button>
                    <button className="sslp-modal-btn-primary" disabled={!name.trim()} onClick={handleSave}>
                        Save
                    </button>
                </div>
            </div>
        </SslModalOverlay>
    );
};

// ---------------------------------------------------------------------------
// Edit Task Modal (view/edit assignments, rename)
// ---------------------------------------------------------------------------

interface EditTaskLibModalProps {
    visible: boolean;
    taskId: string;
    currentName: string;
    categoryId: string;
    categoryName: string;
    classificationName: string;
    skillCategory: SkillCategory | undefined;
    onLinkEquipmentCategory: (equipmentCategoryId: string) => Promise<void>;
    onClose: () => void;
    onSave: (
        taskId: string,
        name: string,
        machineIds: string[],
        addUserIds: string[],
        removeUserIds: string[],
    ) => void;
}

const EditTaskLibModal = ({
    visible,
    taskId,
    currentName,
    categoryId,
    categoryName,
    classificationName,
    skillCategory,
    onLinkEquipmentCategory,
    onClose,
    onSave,
}: EditTaskLibModalProps) => {
    const { allCompanyUsers } = useUserContext();
    const { skillAssignments, subCategories } = useUserModule();
    const { companyUnits, companyEquipmentCategories } = useEquipmentContext();
    const [name, setName] = useState(currentName);
    const [selectedMachines, setSelectedMachines] = useState<MachinePick[]>([]);
    const [equipmentCategoryIdOverride, setEquipmentCategoryIdOverride] = useState<string | null | undefined>(undefined);

    const effectiveSkillCategory = useMemo((): SkillCategory | undefined => {
        if (!skillCategory) return undefined;
        if (equipmentCategoryIdOverride !== undefined) {
            return { ...skillCategory, equipmentCategoryId: equipmentCategoryIdOverride };
        }
        return skillCategory;
    }, [skillCategory, equipmentCategoryIdOverride]);

    const machineOptions = useMemo(
        () => getMachineSuggestOptions(effectiveSkillCategory, companyUnits, subCategories),
        [effectiveSkillCategory, companyUnits, subCategories],
    );

    const equipmentCategoryName = useMemo(() => {
        const equipmentCategoryId = effectiveSkillCategory?.equipmentCategoryId;
        if (!equipmentCategoryId) return '';
        return companyEquipmentCategories.find((c) => c.id === equipmentCategoryId)?.name || '';
    }, [effectiveSkillCategory?.equipmentCategoryId, companyEquipmentCategories]);

    const handleLinkEquipmentCategory = async (equipmentCategoryId: string) => {
        await onLinkEquipmentCategory(equipmentCategoryId);
        setEquipmentCategoryIdOverride(equipmentCategoryId);
    };

    // Compute which users are currently assigned to this task (unique users)
    const currentlyAssigned = useMemo(() => {
        return new Set(skillAssignments.filter((a) => a.taskId === taskId).map((a) => a.userId));
    }, [skillAssignments, taskId]);

    const [assignedUsers, setAssignedUsers] = useState<Set<string>>(new Set(currentlyAssigned));

    // Sync state when modal opens or taskId changes
    useEffect(() => {
        if (visible) {
            setName(currentName);
            setEquipmentCategoryIdOverride(undefined);
            setAssignedUsers(new Set(skillAssignments.filter((a) => a.taskId === taskId).map((a) => a.userId)));
            const existingSubCatIds = [
                ...new Set(
                    skillAssignments.filter((a) => a.taskId === taskId && a.subCategoryId).map((a) => a.subCategoryId!),
                ),
            ];
            const resolved: MachinePick[] = existingSubCatIds.map((id) => {
                const opt = machineOptions.find((o) => o.value === id);
                if (opt) return { id, name: opt.label };
                const unit = companyUnits.find((u) => u.id === id);
                if (unit) return { id, name: (unit.name && unit.name.trim()) || id };
                const sc = subCategories.find((s) => s.id === id);
                if (sc) return { id, name: sc.name };
                return { id, name: id };
            });
            setSelectedMachines(resolved);
        }
    }, [visible, taskId]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleToggleUser = (userId: string) => {
        setAssignedUsers((prev) => {
            const next = new Set(prev);
            if (next.has(userId)) next.delete(userId);
            else next.add(userId);
            return next;
        });
    };

    const handleToggleAll = () => {
        if (assignedUsers.size === allCompanyUsers.length) {
            setAssignedUsers(new Set());
        } else {
            setAssignedUsers(new Set(allCompanyUsers.map((u) => u.id)));
        }
    };

    const handleSave = () => {
        if (!name.trim()) return;
        const addUserIds = Array.from(assignedUsers).filter((id) => !currentlyAssigned.has(id));
        const removeUserIds = Array.from(currentlyAssigned).filter((id) => !assignedUsers.has(id));
        onSave(
            taskId,
            name.trim(),
            selectedMachines.map((m) => m.id),
            addUserIds,
            removeUserIds,
        );
        onClose();
    };

    const getInitials = (n: string | null | undefined): string => {
        if (!n) return '?';
        const parts = n.trim().split(' ');
        if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
        return (parts[0][0] || '?').toUpperCase();
    };

    if (!visible) return null;

    return (
        <SslModalOverlay onDismiss={onClose}>
            <div className="sslp-modal sslp-modal-wide">
                <div className="sslp-modal-header">
                    <h3>Edit Task</h3>
                    <button className="sslp-modal-close" onClick={onClose}>
                        ×
                    </button>
                </div>
                <div className="sslp-modal-body">
                    <p className="sslp-modal-context">
                        <strong>Category:</strong> {categoryName} &middot; <strong>Classification:</strong>{' '}
                        {classificationName}
                    </p>

                    <label className="sslp-modal-label" htmlFor="skillset-edit-task-name">
                        TASK NAME *
                    </label>
                    <input
                        id="skillset-edit-task-name"
                        className="sslp-modal-input"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        autoFocus
                    />

                    <MachinePillsSearchSuggest
                        skillCategoryId={categoryId}
                        equipmentCategoryId={effectiveSkillCategory?.equipmentCategoryId}
                        equipmentCategoryName={equipmentCategoryName}
                        machineOptions={machineOptions}
                        selected={selectedMachines}
                        onSelectedChange={setSelectedMachines}
                        inputId="skillset-edit-task-machine"
                        onLinkEquipmentCategory={handleLinkEquipmentCategory}
                    />

                    <label className="sslp-modal-label">ASSIGNED USERS</label>
                    <div className="sslp-user-toggle-list">
                        <div className="sslp-user-toggle-row sslp-toggle-all">
                            <span>Select All / None</span>
                            <button
                                type="button"
                                className={`sslp-toggle-switch ${assignedUsers.size === allCompanyUsers.length ? 'active' : ''}`}
                                onClick={handleToggleAll}
                            />
                        </div>
                        {allCompanyUsers.map((user) => (
                            <div key={user.id} className="sslp-user-toggle-row">
                                <div className="sslp-user-toggle-info">
                                    <div className="sslp-user-avatar-sm">{getInitials(user.name)}</div>
                                    <div>
                                        <div className="sslp-user-toggle-name">{user.name || '—'}</div>
                                        <div className="sslp-user-toggle-role">{user.role || ''}</div>
                                    </div>
                                </div>
                                <button
                                    type="button"
                                    className={`sslp-toggle-switch ${assignedUsers.has(user.id) ? 'active' : ''}`}
                                    onClick={() => handleToggleUser(user.id)}
                                />
                            </div>
                        ))}
                    </div>
                </div>
                <div className="sslp-modal-footer">
                    <button className="sslp-modal-btn-cancel" onClick={onClose}>
                        Cancel
                    </button>
                    <button className="sslp-modal-btn-primary" disabled={!name.trim()} onClick={handleSave}>
                        Save Changes
                    </button>
                </div>
            </div>
        </SslModalOverlay>
    );
};

// ---------------------------------------------------------------------------
// Inner component (uses context)
// ---------------------------------------------------------------------------

const SkillsetSetupLibraryPageInner = () => {
    const { company, companyOwner, allCompanyUsers } = useUserContext();
    const {
        classifications,
        categories,
        tasks,
        subCategories,
        skillAssignments,
        loading,
        createClassification,
        updateClassification,
        deleteClassification,
        createCategory,
        updateCategory,
        deleteCategory,
        createTask,
        updateTask,
        deleteTask,
        assignSkill,
        deleteSkillAssignment,
        skillLevels,
        createSkillLevel,
        updateSkillLevel,
        deleteSkillLevel,
    } = useUserModule();
    const { companyUnits } = useEquipmentContext();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();

    const companyId = (company?.id as string) || companyOwner?.companyId || '';

    const [activeTab, setActiveTab] = useState<SkillsetsPageTab>(() =>
        searchParams.get('tab') === 'user-proficiencies' ? 'user-proficiencies' : 'classifications',
    );

    const [gradeUserNameFilter, setGradeUserNameFilter] = useState('');

    const gradeUserColumns = useMemo(
        () => getGradeUserColumns(navigate, gradeUserNameFilter, setGradeUserNameFilter),
        [navigate, gradeUserNameFilter],
    );

    // Modal state — Add
    const [showAddClassification, setShowAddClassification] = useState(false);
    const [addCategoryTarget, setAddCategoryTarget] = useState<{
        classificationId: string;
        classificationName: string;
    } | null>(null);
    const [addTaskTarget, setAddTaskTarget] = useState<{
        categoryId: string;
        categoryName: string;
        classificationName: string;
    } | null>(null);

    // Modal state — Edit
    const [editClassificationTarget, setEditClassificationTarget] = useState<{ id: string; name: string } | null>(null);
    const [editCategoryTarget, setEditCategoryTarget] = useState<{
        id: string;
        name: string;
        equipmentCategoryId: string | null;
        classificationName: string;
    } | null>(null);
    const [editTaskTarget, setEditTaskTarget] = useState<{
        id: string;
        name: string;
        categoryId: string;
        categoryName: string;
        classificationName: string;
    } | null>(null);

    // Delete confirmation
    const [deleteTarget, setDeleteTarget] = useState<{
        type: string;
        id: string;
        name: string;
        hasChildren: boolean;
    } | null>(null);

    const [expandedClassificationIds, setExpandedClassificationIds] = useState<string[]>(() =>
        companyId ? readStoredExpandedClassifications(companyId) : [],
    );

    // Machine # filter scoped per category (avoids emptying other accordion sections)
    const [filterSubCategoryByCategory, setFilterSubCategoryByCategory] = useState<
        Record<string, string | null>
    >({});

    useEffect(() => {
        setExpandedClassificationIds(companyId ? readStoredExpandedClassifications(companyId) : []);
    }, [companyId]);

    // Derived data
    const totalCategories = categories.length;
    const totalTasks = tasks.length;

    const confirmedUsers = useMemo(() => {
        return allCompanyUsers
            .filter((user) => !isUnlinkedInvite(user.cognitoId))
            .slice()
            .sort((a, b) => {
                const aName = (a.name?.trim() || a.email || '').toLowerCase();
                const bName = (b.name?.trim() || b.email || '').toLowerCase();
                return aName.localeCompare(bName, undefined, { sensitivity: 'base' });
            });
    }, [allCompanyUsers]);

    const filteredGradeUsers = useMemo(() => {
        const query = gradeUserNameFilter.trim().toLowerCase();
        if (!query) return confirmedUsers;
        return confirmedUsers.filter((user) => {
            const name = (user.name?.trim() || user.email || '').toLowerCase();
            return name.includes(query);
        });
    }, [confirmedUsers, gradeUserNameFilter]);

    const handleExpandedClassificationIdsChange = (ids: string[]) => {
        const visibleIds = new Set(classifications.map((c) => c.id));
        const hiddenStillExpanded = expandedClassificationIds.filter((id) => !visibleIds.has(id));
        const next = [...hiddenStillExpanded, ...ids];
        setExpandedClassificationIds(next);
        if (companyId) writeStoredExpandedClassifications(companyId, next);
    };

    // Get sub-category options for a given skill category (from connected equipment category units + manual sub-categories)
    const getSubCategoryOptions = (category: SkillCategory) => {
        const options: { id: string; name: string }[] = [];
        if (category.equipmentCategoryId) {
            // Include equipment units from the connected equipment category
            const units = companyUnits.filter((u) => u.categoryId === category.equipmentCategoryId);
            options.push(...units.map((u) => ({ id: u.id, name: u.name || u.id })));
        }
        // Always include manually-created sub-categories for this skill category
        const manualSubs = subCategories
            .filter((sc) => sc.categoryId === category.id)
            .map((sc) => ({ id: sc.id, name: sc.name }));
        // Avoid duplicates by ID
        for (const sub of manualSubs) {
            if (!options.some((o) => o.id === sub.id)) {
                options.push(sub);
            }
        }
        return options;
    };

    // Handlers
    const handleAddClassification = async (name: string) => {
        await createClassification(name);
    };

    const handleAddCategory = async (name: string, equipmentCategoryId: string | null) => {
        if (!addCategoryTarget) return;
        await createCategory({
            classificationId: addCategoryTarget.classificationId,
            companyId,
            name,
            allowMachineNumber: !!equipmentCategoryId,
            equipmentCategoryId,
        });
    };

    const handleAddTask = async (name: string, machineIds: string[], assignedUserIds: string[]) => {
        if (!addTaskTarget) return;
        try {
            // Create the task in the library
            await createTask({
                categoryId: addTaskTarget.categoryId,
                companyId,
                name,
            });

            // Find the newly created task
            const { skillTasksByCategoryId } = await import('../../graphql/userModuleQueries');
            const categoryTasks = await skillTasksByCategoryId(addTaskTarget.categoryId);
            const newTask = categoryTasks.find((t: any) => t.name === name);
            if (!newTask) return;

            // Machine picks are SingleUnit ids from SearchSuggest (no SubCategory create-from-type)
            const resolvedSubCategoryIds = [...new Set(machineIds.filter(Boolean))];

            // Create user assignments
            if (assignedUserIds.length > 0) {
                // If machine numbers exist, create one assignment per machine # per user
                // If no machine numbers, create one assignment per user with null subCategoryId
                const subIdsToAssign: (string | null)[] =
                    resolvedSubCategoryIds.length > 0 ? resolvedSubCategoryIds : [null];
                for (const userId of assignedUserIds) {
                    for (const subCatId of subIdsToAssign) {
                        try {
                            await assignSkill({
                                userId,
                                taskId: newTask.id,
                                companyId,
                                subCategoryId: subCatId,
                                skillLevel: 'Not Evaluated',
                                evaluatedBy: null,
                                evaluatedByName: null,
                                evaluationDate: null,
                            });
                        } catch (e) {
                            console.error('Failed to assign task to user:', e);
                        }
                    }
                }
            }
        } catch (e) {
            console.error('Failed to create task:', e);
        }
    };

    const handleRequestDelete = (type: string, id: string, name: string) => {
        let hasChildren = false;
        if (type === 'classification') hasChildren = categories.some((c) => c.classificationId === id);
        else if (type === 'category') hasChildren = tasks.some((t) => t.categoryId === id);
        setDeleteTarget({ type, id, name, hasChildren });
    };

    const handleConfirmDelete = async () => {
        if (!deleteTarget) return;
        const { type, id } = deleteTarget;
        if (type === 'classification') await deleteClassification(id);
        else if (type === 'category') await deleteCategory(id);
        else if (type === 'task') await deleteTask(id);
        setDeleteTarget(null);
    };

    // Edit handlers
    const handleEditClassification = async (id: string, name: string) => {
        await updateClassification(id, name);
    };

    const handleEditCategory = async (id: string, name: string, equipmentCategoryId: string | null) => {
        // Update name and allowMachineNumber via context
        await updateCategory(id, name, !!equipmentCategoryId);
        // Also update equipmentCategoryId directly via GraphQL (context doesn't pass this field through)
        const { updateSkillCategory } = await import('../../graphql/userModuleQueries');
        await updateSkillCategory({ id, equipmentCategoryId });
    };

    const handleLinkSkillCategoryEquipment = useCallback(
        async (skillCategoryId: string, equipmentCategoryId: string) => {
            const cat = categories.find((c) => c.id === skillCategoryId);
            if (!cat) return;
            await updateCategory(skillCategoryId, cat.name, true);
            const { updateSkillCategory } = await import('../../graphql/userModuleQueries');
            await updateSkillCategory({
                id: skillCategoryId,
                equipmentCategoryId,
                allowMachineNumber: true,
            });
        },
        [categories, updateCategory],
    );

    // Skill Level CRUD state
    const [editingSkillLevelId, setEditingSkillLevelId] = useState<string | null>(null);
    const [editingSkillLevelName, setEditingSkillLevelName] = useState('');
    const [editingSkillLevelDesc, setEditingSkillLevelDesc] = useState('');
    const [editingSkillLevelColor, setEditingSkillLevelColor] = useState('green');
    const [newSkillLevelName, setNewSkillLevelName] = useState('');
    const [newSkillLevelDesc, setNewSkillLevelDesc] = useState('');
    const [newSkillLevelColor, setNewSkillLevelColor] = useState('green');
    const [showAddSkillLevel, setShowAddSkillLevel] = useState(false);

    // Default levels shown when company hasn't created any yet
    const defaultLevelNames = ['Good', 'Average', 'Developing', 'Not Evaluated'];
    const defaultLevelDescriptions: Record<string, string> = {
        Good: 'Meets or exceeds expectations consistently',
        Average: 'Competent, occasional guidance needed',
        Developing: 'In training, requires supervision',
        'Not Evaluated': 'Assigned but not yet rated',
    };
    const defaultLevelColors = DEFAULT_LEVEL_COLORS;

    // Ensure defaults are seeded into the DB before making any edit
    const seedDefaultLevels = async (): Promise<void> => {
        if (skillLevels.length > 0) return; // already seeded
        for (let i = 0; i < defaultLevelNames.length; i++) {
            await createSkillLevel({
                companyId,
                name: defaultLevelNames[i],
                description: defaultLevelDescriptions[defaultLevelNames[i]],
                color: defaultLevelColors[defaultLevelNames[i]] || 'gray',
                sortOrder: i + 1,
            });
        }
    };

    const handleAddSkillLevel = async () => {
        if (!newSkillLevelName.trim()) return;
        await seedDefaultLevels();
        const nextOrder =
            skillLevels.length > 0
                ? Math.max(...skillLevels.map((sl) => sl.sortOrder)) + 1
                : defaultLevelNames.length + 1;
        await createSkillLevel({
            companyId,
            name: newSkillLevelName.trim(),
            description: newSkillLevelDesc.trim() || undefined,
            color: newSkillLevelColor,
            sortOrder: nextOrder,
        });
        setNewSkillLevelName('');
        setNewSkillLevelDesc('');
        setNewSkillLevelColor('green');
        setShowAddSkillLevel(false);
    };

    const handleUpdateSkillLevel = async (id: string) => {
        if (!editingSkillLevelName.trim()) return;
        await updateSkillLevel(
            id,
            editingSkillLevelName.trim(),
            undefined,
            editingSkillLevelDesc,
            editingSkillLevelColor,
        );
        setEditingSkillLevelId(null);
        setEditingSkillLevelName('');
        setEditingSkillLevelDesc('');
        setEditingSkillLevelColor('green');
    };

    // For "virtual" default levels (not yet in DB): seed all defaults, then update the specific one
    const handleUpdateDefaultLevel = async (defaultName: string, newName: string) => {
        if (!newName.trim()) return;
        // Seed all defaults into DB
        for (let i = 0; i < defaultLevelNames.length; i++) {
            await createSkillLevel({
                companyId,
                name: defaultLevelNames[i],
                description: defaultLevelDescriptions[defaultLevelNames[i]],
                sortOrder: i + 1,
            });
        }
        // Now find the one we just created and rename it
        // After seeding, skillLevels state will have been updated by createSkillLevel
        // But we need the ID — since createSkillLevel adds to state, we can find it by name
        // However state might not be updated synchronously. Use a workaround:
        // Look for it in the freshly populated skillLevels on next tick
        if (newName.trim() !== defaultName) {
            // The skill level was just created with the defaultName — we need its ID
            // Since createSkillLevel updates state, after the loop completes the state should have them
            // But React batches state... let's find it from the context after a micro-delay
            requestAnimationFrame(() => {
                const created = skillLevels.find((sl) => sl.name === defaultName);
                if (created) {
                    updateSkillLevel(created.id, newName.trim());
                }
            });
        }
        setEditingSkillLevelId(null);
        setEditingSkillLevelName('');
    };

    const handleDeleteSkillLevel = async (id: string) => {
        await deleteSkillLevel(id);
    };

    // ─── Skill Level drag-and-drop reorder ───
    const skillLevelSensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
    );

    const handleSkillLevelDragEnd = useCallback(
        async (event: DragEndEvent) => {
            const { active, over } = event;
            if (!over || active.id === over.id) return;

            // Only operate on real (persisted) levels, excluding "Not Evaluated"
            const draggableLevels = skillLevels
                .filter((sl) => sl.name !== 'Not Evaluated')
                .sort((a, b) => a.sortOrder - b.sortOrder);

            const oldIndex = draggableLevels.findIndex((sl) => sl.id === active.id);
            const newIndex = draggableLevels.findIndex((sl) => sl.id === over.id);
            if (oldIndex === -1 || newIndex === -1) return;

            const reordered = arrayMove(draggableLevels, oldIndex, newIndex);

            // Compute new sortOrder values (1-based, leaving room for "Not Evaluated" at the end)
            const updates: { id: string; newSortOrder: number }[] = [];
            reordered.forEach((level, idx) => {
                const newSortOrder = idx + 1;
                if (level.sortOrder !== newSortOrder) {
                    updates.push({ id: level.id, newSortOrder });
                }
            });

            // Persist only the levels whose sortOrder actually changed
            await Promise.all(
                updates.map(({ id, newSortOrder }) =>
                    updateSkillLevel(id, skillLevels.find((sl) => sl.id === id)!.name, newSortOrder),
                ),
            );
        },
        [skillLevels, updateSkillLevel],
    );

    // The display list: use real skillLevels if they exist, otherwise show virtual defaults
    const displayLevels = sortSkillLevels(
        skillLevels.length > 0
            ? skillLevels
            : defaultLevelNames.map((name, i) => ({
                  id: `default-${i}`,
                  companyId,
                  name,
                  description: defaultLevelDescriptions[name] || null,
                  color: defaultLevelColors[name] || 'gray',
                  sortOrder: i + 1,
              })),
    );

    const handleEditTask = async (
        taskId: string,
        name: string,
        machineIds: string[],
        addUserIds: string[],
        removeUserIds: string[],
    ) => {
        // Rename the task if changed
        const existingTask = tasks.find((t) => t.id === taskId);
        if (existingTask && existingTask.name !== name) {
            await updateTask(taskId, name);
        }

        // Machine picks are unit (or legacy SubCategory) ids from the modal — no create-from-type
        const resolvedSubCategoryIds = [...new Set(machineIds.filter(Boolean))];

        // Determine which subCategoryIds already exist as assignments for this task
        const existingAssignments = skillAssignments.filter((a) => a.taskId === taskId);
        const existingSubIds = new Set(existingAssignments.filter((a) => a.subCategoryId).map((a) => a.subCategoryId!));

        // Add assignments for new sub-categories to existing users only
        const existingUserIds = new Set(existingAssignments.map((a) => a.userId));
        for (const uid of removeUserIds) existingUserIds.delete(uid);

        for (const subId of resolvedSubCategoryIds) {
            if (!existingSubIds.has(subId)) {
                // This is a new machine # — create assignment for each existing user
                for (const userId of existingUserIds) {
                    // Skip if already has this exact combo
                    const alreadyHas = existingAssignments.some(
                        (a) => a.userId === userId && a.subCategoryId === subId,
                    );
                    if (!alreadyHas) {
                        try {
                            await assignSkill({
                                userId,
                                taskId,
                                companyId,
                                subCategoryId: subId,
                                skillLevel: 'Not Evaluated',
                                evaluatedBy: null,
                                evaluatedByName: null,
                                evaluationDate: null,
                            });
                        } catch (e) {
                            console.error('Failed to assign task to user:', e);
                        }
                    }
                }
            }
        }

        // Remove sub-categories that were removed from pills
        const resolvedSubIdSet = new Set(resolvedSubCategoryIds);
        for (const assignment of existingAssignments) {
            if (assignment.subCategoryId && !resolvedSubIdSet.has(assignment.subCategoryId)) {
                // This sub-category was removed — delete the assignment
                try {
                    await deleteSkillAssignment(assignment.id);
                } catch (e) {
                    console.error('Failed to remove sub-category assignment:', e);
                }
            }
        }

        // Add new user assignments (for users newly toggled on)
        for (const userId of addUserIds) {
            // Create one assignment per machine # (or one with null if no machine numbers)
            const subIdsForNewUser: (string | null)[] =
                resolvedSubCategoryIds.length > 0 ? resolvedSubCategoryIds : [null];
            for (const subId of subIdsForNewUser) {
                try {
                    await assignSkill({
                        userId,
                        taskId,
                        companyId,
                        subCategoryId: subId,
                        skillLevel: 'Not Evaluated',
                        evaluatedBy: null,
                        evaluatedByName: null,
                        evaluationDate: null,
                    });
                } catch (e) {
                    console.error('Failed to assign task to user:', e);
                }
            }
        }

        // Remove user assignments (for users toggled off)
        for (const userId of removeUserIds) {
            const userAssignmentsForTask = existingAssignments.filter((a) => a.userId === userId);
            for (const assignment of userAssignmentsForTask) {
                try {
                    await deleteSkillAssignment(assignment.id);
                } catch (e) {
                    console.error('Failed to remove assignment:', e);
                }
            }
        }
    };

    // Loading
    if (loading) {
        return (
            <div className="page-container sslp-page">
                <div className="sslp-loading">Loading skillset library...</div>
            </div>
        );
    }

    return (
        <div className="page-container sslp-page">
            {/* Header */}
            <PageHeader
                title="User Skillsets"
                signalLine={`${classifications.length} Classifications · ${totalCategories} Categories · ${totalTasks} Tasks`}
                actions={
                    activeTab === 'classifications' ? (
                    <Btn
                        variant="primary"
                        buttonIcon={<IconPlus />}
                        buttonText="Add Classification"
                        buttonOnClick={() => setShowAddClassification(true)}
                    />
                    ) : undefined
                }
            />

            <TabNav
                id={SKILLSETS_TAB_NAV_ID}
                variant="pills"
                ariaLabel="User skillsets sections"
                className="tab-nav-stand-alone sslp-tabs"
                tabs={[...SKILLSETS_TABS]}
                activeTabId={activeTab}
                onTabChange={(tabId) => {
                    const nextTab = tabId as SkillsetsPageTab;
                    setActiveTab(nextTab);
                    setSearchParams(nextTab === 'classifications' ? {} : { tab: nextTab }, { replace: true });
                }}
            />

            {activeTab === 'classifications' && (
            <div
                className="sslp-tab-panel"
                {...getTabPanelProps(SKILLSETS_TAB_NAV_ID, 'classifications')}
            >
            {/* Info Banner */}
            <div className="sslp-info-banner">
                <span className="sslp-info-banner-icon">ℹ️</span>
                <span className="sslp-info-banner-text">
                    Build the company-wide library of Classifications, Categories, and Tasks. Assign them to individual
                    users from each user's profile. Changes here affect all future assignments but don't modify existing
                    records already saved to a user's profile.
                </span>
            </div>

            {/* Classification Cards */}
            {classifications.length === 0 ? (
                <div className="sslp-empty-state">
                    <p>No Classifications in this library yet.</p>
                    <p>Add your first Skill Classification to get started.</p>
                    <Btn
                        variant="primary"
                        buttonIcon={<IconPlus />}
                        buttonText="Add Classification"
                        buttonOnClick={() => setShowAddClassification(true)}
                    />
                </div>
            ) : (
                <>
            <AccordionCard
                className="sslp-classifications-list"
                expandMode="multiple"
                expandedIds={expandedClassificationIds}
                onExpandedIdsChange={handleExpandedClassificationIdsChange}
                items={classifications.map((classification): AccordionCardItem => {
                    const classCategories = categories.filter((c) => c.classificationId === classification.id);
                    const classTasks = tasks.filter((t) => classCategories.some((c) => c.id === t.categoryId));
                    const classUsers = new Set(
                        skillAssignments.filter((a) => classTasks.some((t) => t.id === a.taskId)).map((a) => a.userId),
                    );

                    return {
                        id: classification.id,
                        title: classification.name,
                        meta: `${classCategories.length} Categories · ${classUsers.size} Users · ${classTasks.length} Tasks`,
                        actions: (
                            <>
                                <Btn
                                    variant="secondary"
                                    buttonSize="sm"
                                    buttonIcon={<IconPlus />}
                                    buttonText="Category"
                                    buttonOnClick={() =>
                                        setAddCategoryTarget({
                                            classificationId: classification.id,
                                            classificationName: classification.name,
                                        })
                                    }
                                />
                                <Btn
                                    variant="secondary"
                                    buttonSize="sm"
                                    buttonIcon={<IconPencil />}
                                    ariaLabel={`Edit ${classification.name}`}
                                    buttonOnClick={() =>
                                        setEditClassificationTarget({
                                            id: classification.id,
                                            name: classification.name,
                                        })
                                    }
                                />
                                <Btn
                                    variant="secondary"
                                    buttonSize="sm"
                                    buttonIcon={<IconTrashEmpty />}
                                    ariaLabel={`Delete ${classification.name}`}
                                    buttonOnClick={() =>
                                        handleRequestDelete(
                                            'classification',
                                            classification.id,
                                            classification.name,
                                        )
                                    }
                                />
                            </>
                        ),
                        children: (
                                    <>
                                    {classCategories.length === 0 && (
                                        <div className="sslp-empty-category">
                                            <p>No Categories in this Classification</p>
                                            <p>Add your first Skills Category to this Classification.</p>
                                            <button
                                                className="sslp-btn-category"
                                                onClick={() =>
                                                    setAddCategoryTarget({
                                                        classificationId: classification.id,
                                                        classificationName: classification.name,
                                                    })
                                                }
                                            >
                                                + Add Category
                                            </button>
                                        </div>
                                    )}

                                    {classCategories.map((category) => {
                                        const categoryTasks = tasks.filter((t) => t.categoryId === category.id);
                                        const subCatOptions = getSubCategoryOptions(category);
                                        // Get sub-category pills: unique sub-categories assigned to tasks in this category
                                        const assignedSubCatIds = new Set(
                                            skillAssignments
                                                .filter(
                                                    (a) =>
                                                        categoryTasks.some((t) => t.id === a.taskId) && a.subCategoryId,
                                                )
                                                .map((a) => a.subCategoryId!),
                                        );
                                        const subCatPills = subCatOptions.filter((sc) => assignedSubCatIds.has(sc.id));

                                        return (
                                            <div key={category.id} className="sslp-category-section">
                                                <div className="sslp-category-header-row">
                                                    <div className="sslp-category-info">
                                                        <span className="sslp-category-name">{category.name}</span>
                                                        {subCatPills.length > 0 && (
                                                            <div
                                                                className="sslp-subcategory-pills"
                                                                role="group"
                                                                aria-label={`${category.name} machine filters`}
                                                            >
                                                                {subCatPills.map((sc) => {
                                                                    const activeFilter =
                                                                        filterSubCategoryByCategory[category.id] ??
                                                                        null;
                                                                    return (
                                                                        <button
                                                                            key={sc.id}
                                                                            type="button"
                                                                            className={`badge badge-neutral btn-sm${activeFilter === sc.id ? ' is-active' : ''}`}
                                                                            aria-pressed={activeFilter === sc.id}
                                                                            onClick={() =>
                                                                                setFilterSubCategoryByCategory(
                                                                                    (prev) => ({
                                                                                        ...prev,
                                                                                        [category.id]:
                                                                                            prev[category.id] === sc.id
                                                                                                ? null
                                                                                                : sc.id,
                                                                                    }),
                                                                                )
                                                                            }
                                                                        >
                                                                            {sc.name}
                                                                        </button>
                                                                    );
                                                                })}
                                                            </div>
                                                        )}
                                                    </div>
                                                    <div className="sslp-category-actions">
                                                        <Btn
                                                            variant="secondary"
                                                            buttonSize="sm"
                                                            buttonIcon={<IconPlus />}
                                                            buttonText="Task"
                                                            buttonOnClick={() =>
                                                                setAddTaskTarget({
                                                                    categoryId: category.id,
                                                                    categoryName: category.name,
                                                                    classificationName: classification.name,
                                                                })
                                                            }
                                                        />
                                                        <button
                                                            className="sslp-btn-icon-edit-sm"
                                                            title="Edit category"
                                                            onClick={() =>
                                                                setEditCategoryTarget({
                                                                    id: category.id,
                                                                    name: category.name,
                                                                    equipmentCategoryId:
                                                                        category.equipmentCategoryId || null,
                                                                    classificationName: classification.name,
                                                                })
                                                            }
                                                        >
                                                            ✏️
                                                        </button>
                                                        <button
                                                            className="sslp-btn-icon-delete-sm"
                                                            title="Delete category"
                                                            onClick={() =>
                                                                handleRequestDelete(
                                                                    'category',
                                                                    category.id,
                                                                    category.name,
                                                                )
                                                            }
                                                        >
                                                            🗑️
                                                        </button>
                                                    </div>
                                                </div>

                                                {/* Tasks */}
                                                {categoryTasks.map((task) => {
                                                    // Get all sub-category pills for this task
                                                    const taskAssignedSubIds = [
                                                        ...new Set(
                                                            skillAssignments
                                                                .filter((a) => a.taskId === task.id && a.subCategoryId)
                                                                .map((a) => a.subCategoryId!),
                                                        ),
                                                    ];
                                                    const taskPills = taskAssignedSubIds.map((subId) => {
                                                        // Try subCatOptions first (includes equipment units + manual subs for this category)
                                                        const opt = subCatOptions.find((o) => o.id === subId);
                                                        if (opt) return opt.name;
                                                        // Fallback: look in the full subCategories list
                                                        const sc = subCategories.find((s) => s.id === subId);
                                                        return sc ? sc.name : subId;
                                                    });
                                                    // Apply filter only within this category
                                                    const activeMachineFilter =
                                                        filterSubCategoryByCategory[category.id] ?? null;
                                                    if (
                                                        activeMachineFilter &&
                                                        !taskAssignedSubIds.includes(activeMachineFilter)
                                                    )
                                                        return null;
                                                    // Count unique users (not raw assignment count)
                                                    const uniqueUserCount = new Set(
                                                        skillAssignments
                                                            .filter((a) => a.taskId === task.id)
                                                            .map((a) => a.userId),
                                                    ).size;
                                                    return (
                                                        <div key={task.id} className="sslp-task-row">
                                                            <div className="sslp-task-info">
                                                                <div className="sslp-task-name-col">
                                                                    <span className="sslp-task-name">{task.name}</span>
                                                                    {taskPills.length > 0 && (
                                                                        <div className="sslp-subcategory-pills sm">
                                                                            {taskPills.map((pill, i) => (
                                                                                <span
                                                                                    key={i}
                                                                                    className="badge badge-neutral btn-sm"
                                                                                >
                                                                                    {pill}
                                                                                </span>
                                                                            ))}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                                {uniqueUserCount > 0 && (
                                                                    <span className="sslp-task-assigned-count">
                                                                        {uniqueUserCount} user
                                                                        {uniqueUserCount !== 1 ? 's' : ''}
                                                                    </span>
                                                                )}
                                                            </div>
                                                            <div className="sslp-task-actions">
                                                                <button
                                                                    className="sslp-btn-icon-edit-sm"
                                                                    title="Edit task / manage assignments"
                                                                    onClick={() =>
                                                                        setEditTaskTarget({
                                                                            id: task.id,
                                                                            name: task.name,
                                                                            categoryId: category.id,
                                                                            categoryName: category.name,
                                                                            classificationName: classification.name,
                                                                        })
                                                                    }
                                                                >
                                                                    ✏️
                                                                </button>
                                                                <button
                                                                    className="sslp-btn-icon-delete-sm"
                                                                    title="Delete task"
                                                                    onClick={() =>
                                                                        handleRequestDelete('task', task.id, task.name)
                                                                    }
                                                                >
                                                                    🗑️
                                                                </button>
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        );
                                    })}
                                    </>
                        ),
                    };
                })}
            />

                {classifications.length > 0 && (
                    <Btn
                        variant="secondary-outlined"
                        buttonIcon={<IconPlus />}
                        buttonText="Add Another Classification"
                        buttonClasses="sslp-btn-add-another"
                        buttonOnClick={() => setShowAddClassification(true)}
                    />
                )}
                </>
            )}

            {/* Skill Levels Section */}
            <Card variant="card-sm" className="sslp-skill-levels-section">
                <div className="sslp-skill-levels-header">
                    <div>
                        <h3 className="sslp-skill-levels-title">Skill Levels</h3>
                        <p className="sslp-skill-levels-subtitle">
                            Company-defined performance ratings used when grading proficiency
                        </p>
                    </div>
                    <Btn
                        variant="primary"
                        buttonSize="sm"
                        buttonIcon={<IconPlus />}
                        buttonText="Add"
                        buttonOnClick={() => setShowAddSkillLevel(true)}
                    />
                </div>
                <div className="sslp-skill-levels-list">
                    <DndContext
                        sensors={skillLevelSensors}
                        collisionDetection={closestCenter}
                        onDragEnd={handleSkillLevelDragEnd}
                    >
                        <SortableContext
                            items={displayLevels
                                .filter((l) => l.name !== 'Not Evaluated' && !l.id.startsWith('default-'))
                                .map((l) => l.id)}
                            strategy={verticalListSortingStrategy}
                        >
                            {displayLevels
                                .filter((l) => l.name !== 'Not Evaluated')
                                .map((level, idx) => {
                                    const isVirtual = level.id.startsWith('default-');
                                    const rowContent = (dragHandleProps?: Record<string, any>) => (
                                        <div className={`sslp-skill-level-row ${idx % 2 === 1 ? 'alt' : ''}`}>
                                            {editingSkillLevelId === level.id ? (
                                                <div className="sslp-skill-level-edit-row">
                                                    <input
                                                        className="sslp-skill-level-input"
                                                        placeholder="Level name"
                                                        value={editingSkillLevelName}
                                                        onChange={(e) => setEditingSkillLevelName(e.target.value)}
                                                        autoFocus
                                                    />
                                                    <input
                                                        className="sslp-skill-level-input sslp-skill-level-desc-input"
                                                        placeholder="Description (optional)"
                                                        value={editingSkillLevelDesc}
                                                        onChange={(e) => setEditingSkillLevelDesc(e.target.value)}
                                                    />
                                                    <div className="sslp-color-swatches">
                                                        {COLOR_OPTIONS.map((colorKey) => (
                                                            <button
                                                                key={colorKey}
                                                                type="button"
                                                                className={`sslp-color-swatch ${editingSkillLevelColor === colorKey ? 'selected' : ''}`}
                                                                style={{ background: SKILL_LEVEL_COLORS[colorKey].dot }}
                                                                onClick={() => setEditingSkillLevelColor(colorKey)}
                                                                title={colorKey}
                                                            />
                                                        ))}
                                                    </div>
                                                    <button
                                                        className="sslp-btn-save-sm"
                                                        onClick={() => {
                                                            if (isVirtual)
                                                                handleUpdateDefaultLevel(
                                                                    level.name,
                                                                    editingSkillLevelName,
                                                                );
                                                            else handleUpdateSkillLevel(level.id);
                                                        }}
                                                    >
                                                        Save
                                                    </button>
                                                    <button
                                                        className="sslp-btn-cancel-sm"
                                                        onClick={() => setEditingSkillLevelId(null)}
                                                    >
                                                        Cancel
                                                    </button>
                                                </div>
                                            ) : (
                                                <>
                                                    {!isVirtual && dragHandleProps && (
                                                        <SkillLevelDragHandle {...dragHandleProps} />
                                                    )}
                                                    <div className="sslp-skill-level-info">
                                                        <span
                                                            className="sslp-skill-level-badge"
                                                            style={{
                                                                background:
                                                                    SKILL_LEVEL_COLORS[level.color || 'gray']?.bg ||
                                                                    '#f1f5f9',
                                                                color:
                                                                    SKILL_LEVEL_COLORS[level.color || 'gray']?.text ||
                                                                    '#334155',
                                                            }}
                                                        >
                                                            {level.name}
                                                        </span>
                                                        {level.description && (
                                                            <span className="sslp-skill-level-description">
                                                                {level.description}
                                                            </span>
                                                        )}
                                                    </div>
                                                    <div className="sslp-skill-level-actions">
                                                        <button
                                                            className="sslp-btn-icon-edit-sm"
                                                            title="Edit"
                                                            onClick={() => {
                                                                setEditingSkillLevelId(level.id);
                                                                setEditingSkillLevelName(level.name);
                                                                setEditingSkillLevelDesc(level.description || '');
                                                                setEditingSkillLevelColor(level.color || 'gray');
                                                            }}
                                                        >
                                                            ✏️
                                                        </button>
                                                        {!isVirtual && (
                                                            <button
                                                                className="sslp-btn-icon-delete-sm"
                                                                title="Delete"
                                                                onClick={() => handleDeleteSkillLevel(level.id)}
                                                            >
                                                                🗑️
                                                            </button>
                                                        )}
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                    );

                                    // Virtual (default) levels are not draggable
                                    if (isVirtual) {
                                        return <div key={level.id}>{rowContent()}</div>;
                                    }

                                    return (
                                        <SortableSkillLevelRow key={level.id} id={level.id}>
                                            {(dragHandleProps) => rowContent(dragHandleProps)}
                                        </SortableSkillLevelRow>
                                    );
                                })}
                        </SortableContext>
                    </DndContext>

                    {/* "Not Evaluated" always pinned at the bottom */}
                    {displayLevels
                        .filter((l) => l.name === 'Not Evaluated')
                        .map((level) => {
                            const isVirtual = level.id.startsWith('default-');
                            return (
                                <div key={level.id} className="sslp-skill-level-row sslp-skill-level-pinned">
                                    {editingSkillLevelId === level.id ? (
                                        <div className="sslp-skill-level-edit-row">
                                            <input
                                                className="sslp-skill-level-input"
                                                placeholder="Level name"
                                                value={editingSkillLevelName}
                                                onChange={(e) => setEditingSkillLevelName(e.target.value)}
                                                autoFocus
                                            />
                                            <input
                                                className="sslp-skill-level-input sslp-skill-level-desc-input"
                                                placeholder="Description (optional)"
                                                value={editingSkillLevelDesc}
                                                onChange={(e) => setEditingSkillLevelDesc(e.target.value)}
                                            />
                                            <div className="sslp-color-swatches">
                                                {COLOR_OPTIONS.map((colorKey) => (
                                                    <button
                                                        key={colorKey}
                                                        type="button"
                                                        className={`sslp-color-swatch ${editingSkillLevelColor === colorKey ? 'selected' : ''}`}
                                                        style={{ background: SKILL_LEVEL_COLORS[colorKey].dot }}
                                                        onClick={() => setEditingSkillLevelColor(colorKey)}
                                                        title={colorKey}
                                                    />
                                                ))}
                                            </div>
                                            <button
                                                className="sslp-btn-save-sm"
                                                onClick={() => {
                                                    if (isVirtual)
                                                        handleUpdateDefaultLevel(level.name, editingSkillLevelName);
                                                    else handleUpdateSkillLevel(level.id);
                                                }}
                                            >
                                                Save
                                            </button>
                                            <button
                                                className="sslp-btn-cancel-sm"
                                                onClick={() => setEditingSkillLevelId(null)}
                                            >
                                                Cancel
                                            </button>
                                        </div>
                                    ) : (
                                        <>
                                            <div className="sslp-skill-level-info">
                                                <span
                                                    className="sslp-skill-level-badge"
                                                    style={{
                                                        background:
                                                            SKILL_LEVEL_COLORS[level.color || 'gray']?.bg || '#f1f5f9',
                                                        color:
                                                            SKILL_LEVEL_COLORS[level.color || 'gray']?.text ||
                                                            '#334155',
                                                    }}
                                                >
                                                    {level.name}
                                                </span>
                                                {level.description && (
                                                    <span className="sslp-skill-level-description">
                                                        {level.description}
                                                    </span>
                                                )}
                                            </div>
                                            <div className="sslp-skill-level-actions">
                                                <button
                                                    className="sslp-btn-icon-edit-sm"
                                                    title="Edit"
                                                    onClick={() => {
                                                        setEditingSkillLevelId(level.id);
                                                        setEditingSkillLevelName(level.name);
                                                        setEditingSkillLevelDesc(level.description || '');
                                                        setEditingSkillLevelColor(level.color || 'gray');
                                                    }}
                                                >
                                                    ✏️
                                                </button>
                                            </div>
                                        </>
                                    )}
                                </div>
                            );
                        })}
                </div>
                {showAddSkillLevel && (
                    <div className="sslp-skill-level-add-row">
                        <input
                            className="sslp-skill-level-input"
                            placeholder="New skill level name..."
                            value={newSkillLevelName}
                            onChange={(e) => setNewSkillLevelName(e.target.value)}
                            autoFocus
                        />
                        <input
                            className="sslp-skill-level-input sslp-skill-level-desc-input"
                            placeholder="Description (optional)"
                            value={newSkillLevelDesc}
                            onChange={(e) => setNewSkillLevelDesc(e.target.value)}
                        />
                        <div className="sslp-color-swatches">
                            {COLOR_OPTIONS.map((colorKey) => (
                                <button
                                    key={colorKey}
                                    type="button"
                                    className={`sslp-color-swatch ${newSkillLevelColor === colorKey ? 'selected' : ''}`}
                                    style={{ background: SKILL_LEVEL_COLORS[colorKey].dot }}
                                    onClick={() => setNewSkillLevelColor(colorKey)}
                                    title={colorKey}
                                />
                            ))}
                        </div>
                        <button className="sslp-btn-save-sm" onClick={handleAddSkillLevel}>
                            Add
                        </button>
                        <button
                            className="sslp-btn-cancel-sm"
                            onClick={() => {
                                setShowAddSkillLevel(false);
                                setNewSkillLevelName('');
                                setNewSkillLevelDesc('');
                            }}
                        >
                            Cancel
                        </button>
                    </div>
                )}
            </Card>
            </div>
            )}

            {activeTab === 'user-proficiencies' && (
                <div
                    className="sslp-tab-panel"
                    {...getTabPanelProps(SKILLSETS_TAB_NAV_ID, 'user-proficiencies')}
                >
                    <DataTable
                        className="sslp-grade-users-table"
                        caption="Grade User Skillsets"
                        captionVisuallyHidden
                        columns={gradeUserColumns}
                        rows={filteredGradeUsers}
                        getRowKey={(user) => user.id}
                        filterable
                        emptyState={gradeUserNameFilter.trim() ? 'No matching users' : 'No confirmed users'}
                    />
                </div>
            )}

            {/* Modals */}
            <AddClassificationLibModal
                visible={showAddClassification}
                onClose={() => setShowAddClassification(false)}
                onSave={handleAddClassification}
            />

            {addCategoryTarget && (
                <AddCategoryLibModal
                    visible={true}
                    classificationId={addCategoryTarget.classificationId}
                    classificationName={addCategoryTarget.classificationName}
                    onClose={() => setAddCategoryTarget(null)}
                    onSave={handleAddCategory}
                />
            )}

            {addTaskTarget && (
                <AddTaskLibModal
                    visible={true}
                    categoryId={addTaskTarget.categoryId}
                    categoryName={addTaskTarget.categoryName}
                    classificationName={addTaskTarget.classificationName}
                    skillCategory={categories.find((c) => c.id === addTaskTarget.categoryId)}
                    onLinkEquipmentCategory={(equipmentCategoryId) =>
                        handleLinkSkillCategoryEquipment(addTaskTarget.categoryId, equipmentCategoryId)
                    }
                    onClose={() => setAddTaskTarget(null)}
                    onSave={handleAddTask}
                />
            )}

            {/* Delete Confirmation */}
            {deleteTarget && (
                <SslModalOverlay onDismiss={() => setDeleteTarget(null)}>
                    <div className="sslp-modal">
                        <div className="sslp-modal-header">
                            <h3>Delete {deleteTarget.type.charAt(0).toUpperCase() + deleteTarget.type.slice(1)}</h3>
                            <button className="sslp-modal-close" onClick={() => setDeleteTarget(null)}>
                                ×
                            </button>
                        </div>
                        <div className="sslp-modal-body">
                            <p>
                                Are you sure you want to delete <strong>"{deleteTarget.name}"</strong>?
                                {deleteTarget.hasChildren && (
                                    <> This will also remove all associated child items and assignments.</>
                                )}{' '}
                                This action cannot be undone.
                            </p>
                        </div>
                        <div className="sslp-modal-footer">
                            <button className="sslp-modal-btn-cancel" onClick={() => setDeleteTarget(null)}>
                                Cancel
                            </button>
                            <button className="sslp-modal-btn-delete" onClick={handleConfirmDelete}>
                                Delete
                            </button>
                        </div>
                    </div>
                </SslModalOverlay>
            )}

            {/* Edit Classification Modal */}
            {editClassificationTarget && (
                <EditClassificationLibModal
                    visible={true}
                    classificationId={editClassificationTarget.id}
                    currentName={editClassificationTarget.name}
                    onClose={() => setEditClassificationTarget(null)}
                    onSave={handleEditClassification}
                />
            )}

            {/* Edit Category Modal */}
            {editCategoryTarget && (
                <EditCategoryLibModal
                    visible={true}
                    categoryId={editCategoryTarget.id}
                    currentName={editCategoryTarget.name}
                    currentEquipmentCategoryId={editCategoryTarget.equipmentCategoryId}
                    classificationName={editCategoryTarget.classificationName}
                    onClose={() => setEditCategoryTarget(null)}
                    onSave={handleEditCategory}
                />
            )}

            {/* Edit Task Modal (view/manage assignments) */}
            {editTaskTarget && (
                <EditTaskLibModal
                    visible={true}
                    taskId={editTaskTarget.id}
                    currentName={editTaskTarget.name}
                    categoryId={editTaskTarget.categoryId}
                    categoryName={editTaskTarget.categoryName}
                    classificationName={editTaskTarget.classificationName}
                    skillCategory={categories.find((c) => c.id === editTaskTarget.categoryId)}
                    onLinkEquipmentCategory={(equipmentCategoryId) =>
                        handleLinkSkillCategoryEquipment(editTaskTarget.categoryId, equipmentCategoryId)
                    }
                    onClose={() => setEditTaskTarget(null)}
                    onSave={handleEditTask}
                />
            )}
        </div>
    );
};

// ---------------------------------------------------------------------------
// Exported component (wrapped with provider)
// ---------------------------------------------------------------------------

export const SkillsetSetupLibraryPage = () => {
    return (
        <UserModuleProvider>
            <SkillsetSetupLibraryPageInner />
        </UserModuleProvider>
    );
};
